import { Typography } from "@mui/material"
import { useAxesTooltip } from "@mui/x-charts-pro"
import type { HawaiiCovid } from "@phc/common/gen/threatAnalyticsGen"
import { useMemo } from "react"
import { dayjsUTC } from "../../../../utils/dates"
import { extraColors } from "../../../../utils/theme"
import {
  InfoRow,
  ItemContainer,
  ItemHeader,
  TooltipContainer,
  TooltipContentBox,
} from "../../../Charts/LegendStyles"

const TOOLTIP_ITEMS = [
  {
    id: "weekly_cases",
    label: "Weekly Cases",
    valueKey: "weekly_cases",
    toPercent: false,
  },
  {
    id: "rolling_avg_cases_last_7_days",
    label: "7-Day Rolling Avg Cases",
    valueKey: "rolling_avg_cases_last_7_days",
    toPercent: false,
  },
  {
    id: "weekly_test_positivity",
    label: "Weekly Test Positivity %",
    valueKey: "weekly_test_positivity",
    toPercent: true,
  },
  {
    id: "rolling_avg_test_positivity_last_7_days",
    label: "7-Day Rolling Avg Test Positivity %",
    valueKey: "rolling_avg_test_positivity_last_7_days",
    toPercent: true,
  },
] as const

type TooltipItemWithValue = (typeof TOOLTIP_ITEMS)[number] & {
  value: number | null
}

interface TooltipMatches {
  matchingRecords: HawaiiCovid[]
  items: TooltipItemWithValue[]
  isWeekly: boolean
}

const HawaiiCovidTooltipItem = ({ item }: { item: TooltipItemWithValue }) => (
  <ItemContainer>
    <ItemHeader>
      <Typography variant="small1">{item.label}</Typography>
    </ItemHeader>
    <InfoRow>
      <Typography variant="tiny" color={extraColors.medium}>
        Value
      </Typography>
      <Typography variant="tiny">
        {item.value != null
          ? item.toPercent
            ? `${item.value.toFixed(2)}%`
            : item.value.toFixed(2)
          : "N/A"}
      </Typography>
    </InfoRow>
  </ItemContainer>
)

const DAILY_ITEM_IDS = [
  "rolling_avg_cases_last_7_days",
  "rolling_avg_test_positivity_last_7_days",
] as const satisfies readonly (keyof HawaiiCovid)[]

const WEEKLY_ITEM_IDS = ["weekly_cases", "weekly_test_positivity"] as const

const isDailyMode = (itemIds?: readonly string[]) =>
  itemIds?.length === 1 &&
  DAILY_ITEM_IDS.includes(itemIds[0] as (typeof DAILY_ITEM_IDS)[number])

const isWeeklyMode = (itemIds?: readonly string[]) =>
  itemIds?.length === 1 &&
  WEEKLY_ITEM_IDS.includes(itemIds[0] as (typeof WEEKLY_ITEM_IDS)[number])

const NON_MATCHING_RECORD: TooltipMatches = {
  matchingRecords: [],
  items: [],
  isWeekly: false,
}

const getMatchingRecords = (
  rawData: HawaiiCovid[],
  selectedDate: Date | string | number | undefined,
  itemIds?: readonly string[]
) => {
  const matching = isWeeklyMode(itemIds)
    ? rawData.filter(record => {
        const recordDate = dayjsUTC(record.week_ended_at).toDate()
        return (
          selectedDate instanceof Date &&
          recordDate.getTime() === selectedDate.getTime()
        )
      })
    : rawData.filter(record => {
        const selectedDateStr = dayjsUTC(selectedDate).format("YYYY-MM-DD")
        const recordDateStr = record.reported_at
          ? dayjsUTC(record.reported_at).format("YYYY-MM-DD")
          : dayjsUTC(record.week_ended_at).format("YYYY-MM-DD")
        return recordDateStr === selectedDateStr
      })

  return matching
}

export const HawaiiCovidTooltipBody = ({
  rawData,
  hiddenIds,
  itemIds,
}: {
  rawData: HawaiiCovid[] | undefined
  hiddenIds: Set<string>
  itemIds?: readonly string[]
}) => {
  const tooltipData = useAxesTooltip()
  const selectedDate = tooltipData?.[0]?.axisValue

  const { matchingRecords, items, isWeekly } = useMemo((): TooltipMatches => {
    if (!isDailyMode(itemIds) && !isWeeklyMode(itemIds)) {
      return NON_MATCHING_RECORD
    }
    if (!tooltipData || tooltipData.length === 0 || !selectedDate) {
      return NON_MATCHING_RECORD
    }

    const matching = getMatchingRecords(rawData ?? [], selectedDate, itemIds)

    if (matching.length === 0) {
      return NON_MATCHING_RECORD
    }

    const best = matching.reduce((acc, curr) =>
      curr.reported_at &&
      (!acc.reported_at || curr.reported_at > acc.reported_at)
        ? curr
        : acc
    )

    const filteredItems = TOOLTIP_ITEMS.filter(
      item => (!itemIds || itemIds.includes(item.id)) && !hiddenIds.has(item.id)
    ).map(item => {
      const rawVal = best[item.valueKey]
      const value =
        item.toPercent && rawVal != null ? rawVal * 100 : (rawVal ?? null)
      return { ...item, value }
    })

    return {
      matchingRecords: matching,
      items: filteredItems,
      isWeekly: isWeeklyMode(itemIds),
    }
  }, [tooltipData, selectedDate, rawData, hiddenIds, itemIds])

  if (!tooltipData || tooltipData.length === 0 || !selectedDate) return null
  if (matchingRecords.length === 0) return null
  if (items.length === 0) return null

  return (
    <TooltipContainer>
      <TooltipContentBox>
        <Typography align="center" variant="body2Bold">
          {isWeekly
            ? `Week Ended ${dayjsUTC(selectedDate).format("M/DD/YY")}`
            : dayjsUTC(selectedDate).format("M/DD/YY")}
        </Typography>
        {items.map(item => (
          <HawaiiCovidTooltipItem key={item.id} item={item} />
        ))}
      </TooltipContentBox>
    </TooltipContainer>
  )
}
